'use client';

import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import useBoolean from '@/hooks/utils/useBoolean';

import { Icon } from '../ui/icon';

type Props = {
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
};

// Industrial error alert per DESIGN.md:
// - Full treatment: 1px error-toned border + soft error-container tint,
//   never a side-stripe accent or a flooded tonal background.
// - Leading `lucide:circle-alert` at size 5 (20px), tone-matched.
// - `role="alert"` + `aria-live="assertive"` so screen readers announce.
function AlertError(props: Props) {
  const { description, onRetry, retryLabel, title } = props;
  const translate = useTranslations();
  const { value: hasCopied, onTrue: markCopied, onFalse: resetCopied } = useBoolean(false);

  const resolvedTitle = title ?? translate('globalErrorTitle');
  const messageToCopy = description !== undefined && description.length > 0
    ? description
    : title;

  const handleCopy = async () => {
    if (messageToCopy === undefined || messageToCopy.length === 0) {
      return;
    }
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    await navigator.clipboard.writeText(messageToCopy);
    markCopied();
    window.setTimeout(resetCopied, 2000);
  };

  const canCopy = messageToCopy !== undefined && messageToCopy.length > 0;
  const hasDescription = description !== undefined && description.length > 0;
  const hasRetry = onRetry !== undefined;
  const resolvedRetryLabel = retryLabel ?? translate('common.retry');

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="relative w-full rounded-lg border border-error/40 bg-error-container/40 px-3 py-2 md:px-4 md:py-3 flex items-start gap-3 text-on-surface"
    >
      <Icon
        icon="lucide:circle-alert"
        className="size-5 shrink-0 text-error"
        aria-hidden="true"
      />

      <div className="min-w-0 flex-1">
        <p className="type-title-s text-on-surface">{resolvedTitle}</p>
        {hasDescription === true && (
          <p className="type-body-s mt-1 text-on-surface-variant">
            {description}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1 ml-auto">
        {hasRetry === true && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
          >
            {resolvedRetryLabel}
          </Button>
        )}

        {canCopy === true && (
          <Button
            // Keyed on copy state so the icon swap (copy -> check) fades + zooms
            // in instead of snapping; gives the action a confirming beat.
            key={hasCopied === true ? 'copied' : 'idle'}
            variant="ghost"
            size="icon-sm"
            onClick={handleCopy}
            startIcon={hasCopied === true ? 'lucide:check' : 'lucide:copy'}
            tooltipContent={
              hasCopied === true
                ? translate('common.copied')
                : translate('common.copy')
            }
            aria-label={
              hasCopied === true
                ? translate('common.copied')
                : translate('common.copy')
            }
            className={`text-on-surface-variant hover:bg-foreground/8 ${
              hasCopied === true ? 'text-success' : ''
            } animate-in fade-in zoom-in-90 duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:animate-none`}
          />
        )}
      </div>
    </div>
  );
}

export default AlertError;
